import React, { Dispatch, SetStateAction, useEffect, useState } from "react";
import { Collapse, Button } from "react-bootstrap";
import { ActionHandlerFunction, EmailGroup, IActionHandler, AdminUserBase } from "@preveil-api";
import { AdminUIActionTypes, AdminErrorMessages, useCsvDataValidation, FileTemplates, CSVUploadStatusHeading, useAppDispatch, MessageHandlerDisplayType, Message, isSameUser, FileUploadTypes } from "src/common";
import { DraggableContainer, Icon } from "src/components";
import { AddressPanel } from "..";
import { uiActions } from "src/store";
import _ from "lodash";
import { ParseError, ParseMeta } from "papaparse";

type AllProps = {
  aliases: string[];
  is_update_mode?: boolean;
  group_alias?: string;
  selected_group?: EmailGroup;
  org_users: AdminUserBase[];
  valid_group_users: string[];
  is_valid?: boolean;
  setGroupAlias: Dispatch<SetStateAction<string>>;
  handleAction: ActionHandlerFunction;
}

type PapaparseData = {
  data: {
    "User Address": string;
  }
}

// Description: Component for managing user group email group form - csv upload, validation, and submission.
function GroupFormComponent(props: AllProps) {
  const { handleAction, aliases, is_update_mode, group_alias, setGroupAlias, selected_group, org_users, valid_group_users, is_valid } = props;
  const dispatch = useAppDispatch();
  const [open_collapse, setOpenCollapse] = useState<boolean>(false);
  const non_org_group_users_collection: string[] = [];
  const csv_column_name = "User Address";
  const { valid_addresses, invalid_addresses, duplicate_addresses, is_data_drop, handleValidateData, resetData } = useCsvDataValidation(aliases);

  // Description: useEffect to reset data when the component unmounts.
  useEffect(() => {
    return () => {
      resetData();
    };
  }, []);

  const GroupFormRequests = {
    handleCsvData: (csv_file: { data: PapaparseData[], errors: ParseError[], meta: ParseMeta }) => {
      handleCsvFileData(csv_file);
    },
    handleSubmitCsvData: () => {
      if (is_update_mode && !!selected_group) {
        // if Email Group already exists.
        const params = {
          add: valid_addresses,
          remove: [],
          alias: group_alias
        };
        handleAction({ actionType: AdminUIActionTypes.Update, params });
      } else {
        // New user group email creation
        handleAction({
          actionType: AdminUIActionTypes.Create,
          params: { alias: group_alias, members: valid_addresses },
        });
      }
      handleAction({ actionType: AdminUIActionTypes.ShowModal });
      setGroupAlias("");
      resetData();
    },
    handleReset: () => {
      setGroupAlias("");
      resetData();
    },
    handleShowModal: () => {
      setGroupAlias("");
      resetData();
      handleAction({ actionType: AdminUIActionTypes.ShowModal });
    },
    handlePageError: (params: { message: string, stack?: any }) => {
      const { message, stack } = params;
      dispatch(uiActions.handleRequestErrors(new Message(message, MessageHandlerDisplayType.toastr), stack));
    }
  };

  function handlePageActions(action_obj: IActionHandler) {
    const callback = `handle${action_obj.actionType}`;
    if ((GroupFormRequests as any)[callback] instanceof Function) {
      (GroupFormRequests as any)[callback](action_obj.params);
    }
  }

  // After checking if the address is in a valid format, we also need to verify if the address/user is part of the organization.
  valid_addresses.forEach((valid_address: string) => {
    const part_of_org = org_users.find(user => isSameUser(user.user_id, valid_address));
    if (part_of_org) {
      valid_group_users.push(valid_address);
    } else if (!part_of_org) {
      non_org_group_users_collection.push(valid_address);
    }
  });

  // Description: handling csv data.
  function handleCsvFileData(csv_file: { data: PapaparseData[], errors: ParseError[], meta: ParseMeta }) {
    if (csv_file?.errors && csv_file?.errors.length >= 1) {
      // Papaparse returns some errors when the CSV formatting is not correct
      // or the data is missing a delimiter in each row, but that doesn't mean the data will not be parse.
      // so we are just logging those errors.
      handleAction({ actionType: AdminUIActionTypes.PageErrorMessage, params: { message: AdminErrorMessages.error_csv_upload_format, stack: csv_file.errors } });
    }
    const { data, meta } = csv_file;
    const is_valid_columns = meta.fields?.includes(csv_column_name); // we need to verify if the columns name inside the csv file matches the one on the csv template.
    if (data.length === 0 || !is_valid_columns) {
      if (data.length === 0) {
        handleAction({ actionType: AdminUIActionTypes.PageError, params: { message: AdminErrorMessages.error_csv_empty_file, stack: data } });
      } else if (!is_valid_columns) {
        handleAction({ actionType: AdminUIActionTypes.PageError, params: { message: AdminErrorMessages.error_csv_wrong_heading, stack: meta.fields } });
      }
    } else {
      // any repeated address/domain from the csv file will be removed (only within the document).
      const addresses_from_csv_file: string[] = _.uniq(data.map((data_row: any) => data_row[csv_column_name].trim().toLowerCase()));
      handleValidateData(addresses_from_csv_file);
    }
  };

  // Description: Handle delete of a single valid address
  function handleDeleteAction(id?: string) {
    if (!!id) {
      const data = [...valid_addresses, ...invalid_addresses, ...duplicate_addresses].filter((address: string) => address !== id);
      handleValidateData(data);
    }
  };

  return <>
    {valid_group_users.length >= 1 && is_data_drop && (
      <AddressPanel
        title={CSVUploadStatusHeading.valid_group}
        address_list={valid_group_users}
        handleDelete={handleDeleteAction}
      />
    )}
    {!(_.isEmpty(invalid_addresses) && _.isEmpty(duplicate_addresses) && _.isEmpty(non_org_group_users_collection)) && (
      <div className={`invalid-card ${!_.isEmpty(duplicate_addresses) ? "show-divider" : ""}`}>
        <header>
          <h3 className="mb-3">
            Addresses that will <i className="emphasis">not</i> be added
          </h3>
          <Button
            variant="icon"
            onClick={() => setOpenCollapse(!open_collapse)}
            aria-controls="collapse-invalid-area"
          >
            <Icon className={`${open_collapse ? "ficon-chevron-up" : "ficon-chevron-down"}`} />
          </Button>
        </header>
        <Collapse in={open_collapse}>
          <div>
            {duplicate_addresses.length >= 1 && is_data_drop && (
              <AddressPanel
                title={CSVUploadStatusHeading.duplicate_alias}
                address_list={duplicate_addresses}
                error={true}
                handleDelete={handleDeleteAction}
              />
            )}
            {invalid_addresses.length >= 1 && is_data_drop && (
              <AddressPanel
                title={CSVUploadStatusHeading.invalid}
                address_list={invalid_addresses}
                error={true}
                handleDelete={handleDeleteAction}
              />
            )}
            {non_org_group_users_collection.length >= 1 && is_data_drop && (
              <AddressPanel
                title={CSVUploadStatusHeading.non_org_user}
                address_list={non_org_group_users_collection}
                error={true}
                handleDelete={handleDeleteAction}
              />
            )}
          </div>
        </Collapse>
      </div>
    )}
    {!is_data_drop && (
      <div className="mb-3">
        <p>
          Download and use the upload template to accurately add multiple users at once
          <a
            href={FileTemplates.email_group_users}
            target="_blank"
            rel="noreferrer">
            <Icon className="ficon-download small" />
            PreVeil-Group-Email-Upload-Template.csv
          </a>
        </p>
        <DraggableContainer handleAction={handlePageActions} file_upload_type={FileUploadTypes.CSV} />
      </div>
    )}
    <div className="btn-panel">
      <Button id="submit_button"
        onClick={() => GroupFormRequests.handleSubmitCsvData()}
        disabled={_.isEmpty(valid_addresses)}>
        {`${!!is_update_mode ? "Update" : "Create"} User Group`}
      </Button>
      <Button
        id="reset_button"
        disabled={!is_valid}
        variant="no-outline-primary"
        onClick={() => handleAction({ actionType: AdminUIActionTypes.Reset })}>
        Reset
      </Button>
      <Button
        id="cancel_button"
        variant="no-outline-primary"
        onClick={() => handleAction({ actionType: AdminUIActionTypes.ShowModal })} >
        Cancel
      </Button>
    </div>
  </>;
}

export default React.memo(GroupFormComponent);
